import React, { useEffect, useState, useRef } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';

function MenuPopup({ isOpen, onClose, children, title, onBack, showBackButton = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showBackButton && onBack) {
          onBack();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, showBackButton, onBack, onClose]);

  // Prevent pull-to-refresh when at top of scrollable content
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement || !isOpen) return;

    let touchStartY = 0;

    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        touchStartY = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length !== 1) return;

      const touchY = e.touches[0].clientY;
      const touchDelta = touchY - touchStartY;

      // Only prevent pull-to-refresh if:
      // 1. We're at the top of the scrollable content (scrollTop === 0)
      // 2. AND user is dragging down (positive delta)
      if (contentElement.scrollTop === 0 && touchDelta > 0) {
        e.preventDefault();
      }
    };

    contentElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    contentElement.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      contentElement.removeEventListener('touchstart', handleTouchStart);
      contentElement.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[2000] flex items-center justify-center",
        "bg-black/30 transition-opacity duration-200",
        isOpen ? "opacity-100" : "opacity-0"
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (showBackButton && onBack) {
            onBack();
          } else {
            onClose();
          }
        }
      }}
    >
      <div
        className={cn(
          "w-[90%] max-w-4xl h-[85%] bg-white rounded border",
          "shadow-lg flex flex-col overflow-hidden",
          "transition-all duration-200 ease-out",
          isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Simple Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {showBackButton && (
                <button
                  onClick={onBack}
                  className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
              )}
              <h2 className="text-lg sm:text-xl font-medium text-gray-900 truncate">
                {title}
              </h2>
            </div>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded transition-colors flex-shrink-0 ml-2"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div ref={contentRef} className="flex-1 overflow-auto bg-white" style={{ overscrollBehavior: 'contain' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default MenuPopup;